import { useMemo, useState } from 'react';
import {
  Button,
  Divider,
  Form,
  Modal,
  SIZE,
  Stack,
  Text,
  useForm,
} from '@quinyx/ui';
import * as z from 'zod';
import { useNavigate } from 'react-router-dom';

import { TASK_MANAGEMENT_MODULE } from '@/constants/support-task';
import { ICustomer } from '../../api/interfaces/customer';
import {
  useDeleteCustomer,
  useReprovisionCustomer,
  useUndoDeleteCustomer,
} from '../../hooks/customer';
import { useGetNEOModules } from '../../hooks/modules';
import { useUser } from '../../context/authenticatedUser';
import ActionCard from './ActionCard';
import DeleteTaskCard from './DeleteTaskCard';
import { Trans, useTranslate } from '../../hooks/translate';

type ActionsProps = {
  customerId: string;
  customer?: ICustomer;
  isMarkedForDeletion: boolean;
};

const reprovisionSchema = z.object({});
const prepareDeletionSchema = z.object({
  deleteBy: z.string().min(1),
});
const deleteConfirmationSchema = z.object({
  confirmDelete: z.string().min(1),
});

const formatDateForInput = (date: Date) => date.toISOString().split('T')[0];

const Actions = ({
  customerId: domainId,
  customer,
  isMarkedForDeletion,
}: ActionsProps) => {
  const { t } = useTranslate();
  const { systemAdmin } = useUser();
  const { data: neoModules } = useGetNEOModules(domainId);
  const hasTaskManagement = (neoModules ?? []).some(
    ({ moduleName, startDate }: { moduleName: string; startDate?: string }) =>
      moduleName === TASK_MANAGEMENT_MODULE && !!startDate,
  );
  const confirmDeleteWord = t('customerActions.confirmDeleteWord');
  const { reprovisionCustomer, isPending } = useReprovisionCustomer();
  const { deleteCustomer, isLoading: isDeletingCustomer } = useDeleteCustomer();
  const { undoDeleteCustomer, isLoading: isUndoingCustomerDelete } =
    useUndoDeleteCustomer();
  const [isReprovisionModalOpen, setReprovisionModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleteConfirmationModalOpen, setDeleteConfirmationModalOpen] =
    useState(false);
  const [pendingDeleteByDate, setPendingDeleteByDate] = useState<string>();
  const [isDeleteResultModalOpen, setDeleteResultModalOpen] = useState(false);
  const [isUndoConfirmationModalOpen, setUndoConfirmationModalOpen] =
    useState(false);
  const navigate = useNavigate();

  const reprovisionForm = useForm({
    schema: reprovisionSchema,
    defaultValues: {},
  });
  const today = useMemo(() => new Date(), []);
  const defaultDeleteByDate = useMemo(() => {
    const defaultDate = new Date(today);
    defaultDate.setDate(defaultDate.getDate() + 60);
    return formatDateForInput(defaultDate);
  }, [today]);
  const maxDeleteByDate = useMemo(() => {
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + 90);
    return formatDateForInput(maxDate);
  }, [today]);
  const minDeleteByDate = formatDateForInput(today);

  const prepareDeletionForm = useForm({
    schema: prepareDeletionSchema,
    defaultValues: {
      deleteBy: defaultDeleteByDate,
    },
  });
  const deleteConfirmationForm = useForm({
    schema: deleteConfirmationSchema,
    defaultValues: {
      confirmDelete: '',
    },
  });
  const deleteByValue = prepareDeletionForm.watch('deleteBy');
  const deleteConfirmationValue =
    deleteConfirmationForm.watch('confirmDelete') || '';

  const handleReprovision = () => {
    setReprovisionModalOpen(true);
  };

  const handleConfirmReprovision = async () => {
    try {
      await reprovisionCustomer(domainId);
      setReprovisionModalOpen(false);
    } catch {
      // Error toast is already handled by useReprovisionCustomer.
    }
  };

  const handleOpenDeleteModal = () => {
    prepareDeletionForm.reset({
      deleteBy: defaultDeleteByDate,
    });
    setDeleteModalOpen(true);
  };

  const handlePrepareCustomerForDeletion = (
    values: z.infer<typeof prepareDeletionSchema>,
  ) => {
    setPendingDeleteByDate(values.deleteBy);
    setDeleteModalOpen(false);
    deleteConfirmationForm.reset({ confirmDelete: '' });
    setDeleteConfirmationModalOpen(true);
  };

  const handleConfirmCustomerDeletion = async () => {
    if (!customer || !pendingDeleteByDate) return;
    try {
      await deleteCustomer([customer, pendingDeleteByDate, domainId]);
      setDeleteConfirmationModalOpen(false);
      setDeleteResultModalOpen(true);
    } catch {
      // Error toast is already handled by useDeleteCustomer.
    }
  };

  const handleUndoCustomerDeletion = async () => {
    if (!customer) return;
    try {
      await undoDeleteCustomer([customer.customerId, domainId]);
      setDeleteResultModalOpen(false);
      setUndoConfirmationModalOpen(true);
    } catch {
      // Error toast is already handled by useUndoDeleteCustomer.
    }
  };

  return (
    <Stack spacing={SIZE.L}>
      <ActionCard
        title={t('customerActions.reprovisionTitle')}
        description={t('customerActions.reprovisionDescription')}>
        <Form methods={reprovisionForm} onSubmit={handleReprovision}>
          <Form.SubmitButton
            data-test-id="reprovision-customer-button"
            text={t('customerActions.reprovision')}
            isLoading={isPending}
            disabled={isMarkedForDeletion}
          />
        </Form>
      </ActionCard>

      <Divider />

      <ActionCard
        title={
          isMarkedForDeletion
            ? t('customerActions.removeFromDeletion')
            : t('customerActions.prepareForDeletion')
        }
        description={
          isMarkedForDeletion
            ? t('customerActions.removeFromDeletionDescription')
            : t('customerActions.prepareForDeletionDescription')
        }>
        {isMarkedForDeletion ? (
          <Button
            data-test-id="remove-customer-from-deletion-button"
            text={t('customerActions.removeFromDeletion')}
            onClick={handleUndoCustomerDeletion}
            isLoading={isUndoingCustomerDelete}
          />
        ) : (
          <Button
            data-test-id="prepare-customer-for-deletion-button"
            text={t('customerActions.deleteCustomer')}
            intent="danger"
            onClick={handleOpenDeleteModal}
          />
        )}
      </ActionCard>

      {systemAdmin && hasTaskManagement && (
        <>
          <Divider />

          <DeleteTaskCard domainId={domainId} customerName={customer?.name} />
        </>
      )}

      <Modal
        open={isReprovisionModalOpen}
        onOpenChange={setReprovisionModalOpen}>
        <Modal.Header>
          <Modal.CloseButton
            ariaLabel="close-reprovision-customer-modal"
            dataTestId="close-reprovision-customer-modal"
          />
        </Modal.Header>
        <Modal.Content>
          <Modal.HeadlineText>
            {t('customerActions.reprovisionTitle')}
          </Modal.HeadlineText>
          <Stack spacing={SIZE.M} mt={SIZE.S}>
            <Text variant="smallRegular" color="SurfaceOnSurfaceAlt">
              {t('customerActions.reprovisionConfirm')}
            </Text>
            <Stack direction="row" spacing={SIZE.S} justifyContent="flex-end">
              <Button
                data-test-id="cancel-reprovision-customer-button"
                text={t('common.cancel')}
                variant="text"
                onClick={() => setReprovisionModalOpen(false)}
              />
              <Button
                data-test-id="confirm-reprovision-customer-button"
                text={t('customerActions.reprovision')}
                onClick={handleConfirmReprovision}
                isLoading={isPending}
              />
            </Stack>
          </Stack>
        </Modal.Content>
      </Modal>

      <Modal open={isDeleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <Modal.Header>
          <Modal.CloseButton
            ariaLabel="close-delete-customer-modal"
            dataTestId="close-delete-customer-modal"
          />
        </Modal.Header>
        <Modal.Content>
          <Modal.HeadlineText>
            {t('customerActions.prepareForDeletion')}
          </Modal.HeadlineText>
          <Text variant="smallRegular" color="SurfaceOnSurfaceAlt">
            {t('customerActions.prepareForDeletionModalDescription')}
          </Text>
          <Form
            methods={prepareDeletionForm}
            onSubmit={handlePrepareCustomerForDeletion}>
            <Stack spacing={SIZE.M} mt={SIZE.M}>
              <Form.TextInput
                data-test-id="customer-delete-by-date-picker"
                name="deleteBy"
                label={t('customer.deleteBy')}
                type="date"
                min={minDeleteByDate}
                max={maxDeleteByDate}
                required
              />
              <Stack direction="row" spacing={SIZE.S} justifyContent="flex-end">
                <Button
                  data-test-id="cancel-prepare-customer-for-deletion-button"
                  text={t('common.cancel')}
                  variant="text"
                  onClick={() => setDeleteModalOpen(false)}
                />
                <Button
                  data-test-id="confirm-prepare-customer-for-deletion-button"
                  text={t('common.continue')}
                  type="submit"
                  disabled={!deleteByValue}
                />
              </Stack>
            </Stack>
          </Form>
        </Modal.Content>
      </Modal>

      <Modal
        open={isDeleteConfirmationModalOpen}
        onOpenChange={setDeleteConfirmationModalOpen}>
        <Modal.Header>
          <Modal.CloseButton
            ariaLabel="close-delete-customer-confirmation-modal"
            dataTestId="close-delete-customer-confirmation-modal"
          />
        </Modal.Header>
        <Modal.Content>
          <Modal.HeadlineText>
            {t('customerActions.confirmDeletion')}
          </Modal.HeadlineText>
          <Form
            methods={deleteConfirmationForm}
            onSubmit={handleConfirmCustomerDeletion}>
            <Stack spacing={SIZE.M} mt={SIZE.S}>
              <Text variant="smallRegular" color="SurfaceOnSurfaceAlt">
                <Trans
                  i18nKey="customerActions.confirmDeletionDescription"
                  values={{
                    name: customer?.name ?? domainId,
                    confirmWord: confirmDeleteWord,
                  }}
                  components={{ strong: <strong /> }}
                />
              </Text>
              <Form.TextInput
                data-test-id="confirm-customer-deletion-input"
                name="confirmDelete"
                label={t('customerActions.typeDeleteToConfirm', {
                  confirmWord: confirmDeleteWord,
                })}
              />
              <Stack direction="row" spacing={SIZE.S} justifyContent="flex-end">
                <Button
                  data-test-id="cancel-delete-customer-confirmation-button"
                  text={t('common.cancel')}
                  variant="text"
                  onClick={() => setDeleteConfirmationModalOpen(false)}
                />
                <Button
                  data-test-id="confirm-delete-customer-button"
                  text={t('common.delete')}
                  intent="danger"
                  type="submit"
                  isLoading={isDeletingCustomer}
                  disabled={deleteConfirmationValue !== confirmDeleteWord}
                />
              </Stack>
            </Stack>
          </Form>
        </Modal.Content>
      </Modal>

      <Modal
        open={isDeleteResultModalOpen}
        onOpenChange={setDeleteResultModalOpen}>
        <Modal.Header>
          <Modal.CloseButton
            ariaLabel="close-delete-result-modal"
            dataTestId="close-delete-result-modal"
          />
        </Modal.Header>
        <Modal.Content>
          <Modal.HeadlineText>
            {t('customerActions.preparedForDeletion')}
          </Modal.HeadlineText>
          <Stack spacing={SIZE.M} mt={SIZE.S}>
            <Text variant="smallRegular" color="SurfaceOnSurfaceAlt">
              {t('customerActions.preparedForDeletionDescription')}
            </Text>
            <Stack direction="row" spacing={SIZE.S} justifyContent="flex-end">
              <Button
                data-test-id="undo-delete-customer-button"
                text={t('common.undo')}
                variant="text"
                onClick={handleUndoCustomerDeletion}
                isLoading={isUndoingCustomerDelete}
              />
              <Button
                data-test-id="continue-after-delete-customer-button"
                text={t('common.continue')}
                onClick={() => navigate('/customers')}
              />
            </Stack>
          </Stack>
        </Modal.Content>
      </Modal>

      <Modal
        open={isUndoConfirmationModalOpen}
        onOpenChange={setUndoConfirmationModalOpen}>
        <Modal.Header>
          <Modal.CloseButton
            ariaLabel="close-undo-confirmation-modal"
            dataTestId="close-undo-confirmation-modal"
          />
        </Modal.Header>
        <Modal.Content>
          <Modal.HeadlineText>
            {t('customerActions.deleteUndone')}
          </Modal.HeadlineText>
          <Stack spacing={SIZE.M} mt={SIZE.S}>
            <Text variant="smallRegular" color="SurfaceOnSurfaceAlt">
              {t('customerActions.deleteUndoneDescription')}
            </Text>
            <Stack direction="row" spacing={SIZE.S} justifyContent="flex-end">
              <Button
                data-test-id="close-undo-confirmation-button"
                text={t('common.close')}
                onClick={() => setUndoConfirmationModalOpen(false)}
              />
            </Stack>
          </Stack>
        </Modal.Content>
      </Modal>
    </Stack>
  );
};

export default Actions;
